import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import AddIconCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { debounce } from 'lodash'; // Déjà disponible dans package.json
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import useGetQuoteProducts from '../../../../../../hooks/quotes/useGetQuoteProducts';
import { setSnackBar } from '../../../../../../states/app/staticSlice';
import { getCurrencySymbol } from '../Retractions/FeesCalculations';
import { getDiscountQuote } from './DiscountQuotes';

const StepTwo = ({ ticket, formData, handleFormChange }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  // État pour les produits du devis
  const [searchIndex, setSearchIndex] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // ✅ Debounce pour optimiser les requêtes de recherche
  const debouncedSetSearch = useCallback(
    debounce((value) => {
      setDebouncedSearch(value);
    }, 300),
    []
  );

  const { productList, isLoading } = useGetQuoteProducts({
    brandName: 'Casanoov',
    countryCode: ticket?.contact?.store?.country?.code2,
    search: debouncedSearch, // ✅ Utiliser la valeur debouncée
  });

  // ✅ Mettre à jour la recherche debouncée quand inputValue change
  useEffect(() => {
    debouncedSetSearch(inputValue);
  }, [inputValue, debouncedSetSearch]);

  // Calcul des montants sans effet de bord (correction du typage)
  const amounts = useMemo(() => {
    if (formData.lines.length === 0) {
      return {
        priceHt: 0,
        priceTtc: 0,
        priceHtNet: 0,
        priceTtcNet: 0,
        totalVat: 0,
        tvaValue: null,
      };
    } else {
      let tvaValue = null;
      const totalHT = formData.lines.reduce((sum, line) => {
        return sum + line.quantity * line.priceHt;
      }, 0);

      const totalTVA = formData.lines.reduce((sum, line) => {
        tvaValue = line.tvaValue;
        return sum + line.quantity * line.priceHt * (line.tvaValue / 100);
      }, 0);

      const totalTTC = totalHT + totalTVA;

      let discountValue = 0;
      let discountAmount = 0;
      if (ticket?.contact?.store?.contactType?.codeBO === 'P') {
        discountValue = getDiscountQuote(totalHT, formData?.currency);
        discountAmount = totalHT * (discountValue / 100);
      }

      return {
        priceHt: totalHT, // Garder en nombre
        priceTtc: totalTTC, // Garder en nombre
        priceHtNet: totalHT - discountAmount, // Garder en nombre
        priceTtcNet: totalTTC - discountAmount, // Garder en nombre
        totalVat: totalTVA, // Garder en nombre
        tvaValue: tvaValue,
        discount: discountValue,
        discountAmount: discountAmount,
      };
    }
  }, [formData.lines]);

  // Mettre à jour les montants via useEffect
  useEffect(() => {
    if (amounts) {
      handleFormChange({ key: 'amounts', value: amounts });
    }
  }, [amounts, handleFormChange]);

  // Ajouter un nouveau produit
  const handleAddProduct = () => {
    if (searchIndex) {
      let quantity = 1;
      const product = productList.find(
        (product) => product.reference === searchIndex
      );
      if (!product) {
        // Afficher un message d'erreur via la snackBar Redux
        dispatch(
          setSnackBar({
            open: true,
            message: `Aucun produit trouvé avec la référence "${searchIndex}"`,
            color: 'warning',
          })
        );
        return;
      }

      const previousProduct = formData.lines.find(
        (product) => product.reference === searchIndex
      );

      if (previousProduct) {
        quantity = previousProduct.quantity + 1;
      }

      if (product) {
        const newLines = [
          ...formData.lines.filter((line) => line.reference !== searchIndex),
          {
            ...product,
            quantity,
            description: product?.description
              ? product?.description
              : product?.title,
          },
        ];
        handleFormChange({ key: 'lines', value: newLines });
        setSearchIndex('');
        setInputValue('');
      }
    }
  };

  // Supprimer un produit
  const handleDeleteProduct = (productId) => {
    const newLines = formData.lines.filter(
      (line) => line.reference !== productId
    );
    handleFormChange({ key: 'lines', value: newLines });
  };

  // Mettre à jour un produit
  const handleUpdateQuantity = (isMore, reference) => {
    const product = formData.lines.find((line) => line.reference === reference);
    if (product) {
      const newQuantity = isMore ? product.quantity + 1 : product.quantity - 1;

      // Si la quantité atteint 0 ou moins, retirer le produit
      if (newQuantity <= 0) {
        const newLines = formData.lines.filter(
          (line) => line.reference !== reference
        );
        handleFormChange({ key: 'lines', value: newLines });
      } else {
        // Sinon, mettre à jour la quantité
        const newLines = formData.lines.map((line) =>
          line.reference === reference
            ? {
                ...line,
                quantity: newQuantity,
              }
            : line
        );
        handleFormChange({ key: 'lines', value: newLines });
      }
    }
  };

  // Gérer le changement de l'input
  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
    setSearchIndex(newInputValue);
  };

  // Gérer la sélection d'une option
  const handleOptionSelect = (event, value) => {
    setSearchIndex(value);
    setInputValue(value);
  };

  // Gérer l'appui sur Entrée pour ajouter le produit
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault(); // Empêcher le comportement par défaut
      handleAddProduct();
    }
  };

  const tableCellStyle = {
    fontWeight: 'bold',
    fontSize: '0.7em',
    padding: '8px 4px',
  };

  const compactCellStyle = {
    padding: '4px 8px',
    fontSize: '0.8rem',
  };

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
      border: 0,
    },
  }));

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Détails de la commande
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
        }}
      >
        <TableContainer component={Paper}>
          <Table size="small" sx={{ padding: '0px' }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ ...tableCellStyle, padding: '8px 4px' }}
                  align="left"
                >
                  Référence - Titre
                </TableCell>
                <TableCell
                  sx={{ ...tableCellStyle, padding: '8px 4px' }}
                  align="center"
                >
                  Quantité
                </TableCell>
                <TableCell
                  sx={{ ...tableCellStyle, padding: '8px 4px' }}
                  align="right"
                >
                  Prix unitaire HT
                </TableCell>
                <TableCell
                  sx={{ ...tableCellStyle, padding: '8px 4px' }}
                  align="right"
                >
                  Taux de TVA
                </TableCell>
                <TableCell
                  sx={{ ...tableCellStyle, padding: '8px 4px' }}
                  align="right"
                >
                  Prix unitaire TTC
                </TableCell>
                <TableCell
                  sx={{ ...tableCellStyle, padding: '8px 4px' }}
                  align="right"
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {formData.lines.map((line) => (
                <StyledTableRow key={line.reference}>
                  <TableCell sx={compactCellStyle}>
                    <b>{line.reference}</b> - {line.title}
                  </TableCell>
                  <TableCell
                    sx={compactCellStyle}
                    style={{ display: 'flex', alignItems: 'center' }}
                    align="center"
                  >
                    <IconButton
                      onClick={() => handleUpdateQuantity(true, line.reference)}
                      aria-label="delete"
                    >
                      <AddIconCircleIcon size="small" />
                    </IconButton>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {line.quantity}
                    </Typography>
                    <IconButton
                      onClick={() =>
                        handleUpdateQuantity(false, line.reference)
                      }
                      aria-label="delete"
                    >
                      <RemoveCircleIcon size="small" />
                    </IconButton>
                  </TableCell>
                  <TableCell sx={compactCellStyle} align="right">
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {line.priceHt.toFixed(2)}{' '}
                      {getCurrencySymbol(formData.currency)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={compactCellStyle} align="right">
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {line.totalVat} {getCurrencySymbol(formData.currency)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={compactCellStyle} align="right">
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {line.priceTtc.toFixed(2)}{' '}
                      {getCurrencySymbol(formData.currency)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={compactCellStyle} align="center">
                    <IconButton
                      onClick={() => handleDeleteProduct(line.reference)}
                      color="error"
                      size="small"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </StyledTableRow>
              ))}
            </TableBody>

            <TableFooter sx={{ padding: '0px !important' }}>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell
                  sx={compactCellStyle}
                  style={{ marginBottom: '10px' }}
                >
                  <Autocomplete
                    disableClearable
                    disablePortal
                    options={productList || []}
                    getOptionLabel={(option) => option?.reference}
                    getOptionDisabled={(option) => option?.quantity < 1}
                    sx={{ width: '180px' }}
                    inputValue={inputValue}
                    loading={isLoading}
                    renderOption={(props, option) => (
                      <li
                        key={option.reference}
                        {...props}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignContent: 'flex-start',
                          justifyContent: 'flex-start',
                          borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <span
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            fontWeight: 'bold',
                            fontSize: '0.9rem',
                          }}
                        >
                          {option.reference}
                        </span>
                        <span style={{ display: 'flex', fontSize: '0.7rem' }}>
                          {option?.title}
                        </span>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Ajouter un produit"
                        size="small"
                        onKeyDown={handleKeyDown}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isLoading ? (
                                <CircularProgress color="inherit" size={16} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    onInputChange={handleInputChange}
                    onChange={handleOptionSelect}
                    loadingText="Recherche en cours..."
                    noOptionsText={
                      isLoading ? 'Recherche...' : 'Aucun produit trouvé'
                    }
                  />
                </TableCell>
                <TableCell colSpan={5} sx={compactCellStyle} align="left">
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon fontSize="small" />}
                    onClick={handleAddProduct}
                    disabled={!searchIndex}
                    // size="small"
                  >
                    Ajouter
                  </Button>
                </TableCell>
                <TableCell />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <TableContainer component={Paper} sx={{ width: '250px' }}>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell align="left" sx={{ fontWeight: 'bold' }}>
                    Total HT
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {`${amounts?.priceHt?.toFixed(2)} ${getCurrencySymbol(formData?.currency)}`}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="left" sx={{ fontWeight: 'bold' }}>
                    Total TVA ({amounts?.tvaValue}%)
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {`${amounts?.totalVat?.toFixed(2)} ${getCurrencySymbol(formData?.currency)}`}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="left" sx={{ fontWeight: 'bold' }}>
                    Total TTC
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {`${amounts?.priceTtc?.toFixed(2)} ${getCurrencySymbol(formData?.currency)}`}
                  </TableCell>
                </TableRow>
                {ticket?.contact?.store?.contactType?.codeBO === 'P' && (
                  <TableRow>
                    <TableCell align="left" sx={{ fontWeight: 'bold' }}>
                      Réduction ({amounts?.discount}%)
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {`${amounts?.discountAmount?.toFixed(2) || '0.00'} ${getCurrencySymbol(formData?.currency)}`}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Box>
  );
};

export default StepTwo;
